import { useLayoutEffect } from "react";
import type { SortDirection } from "@trackmark/core";
import {
  Outlet,
  RouterProvider,
  createRootRoute,
  createRoute,
  createRouter,
  redirect,
  useNavigate,
  useParams,
  useSearch,
} from "@tanstack/react-router";
import { Rail, TopBar } from "./design/Shell.js";
import { Specimen } from "./design/Specimen.js";
import { AlbumScreen } from "./screens/AlbumScreen.js";
import { ArtistScreen } from "./screens/ArtistScreen.js";
import { BoothScreen } from "./screens/BoothScreen.js";
import { DashboardScreen } from "./screens/DashboardScreen.js";
import { ImportScreen } from "./screens/ImportScreen.js";
import { LIBRARY_TABS, type LibraryTab } from "./screens/Library.js";
import { LibraryScreen } from "./screens/LibraryScreen.js";
import { ListeningScreen } from "./screens/ListeningScreen.js";
import { ListScreen } from "./screens/ListScreen.js";
import { ListsScreen } from "./screens/ListsScreen.js";
import { PlaylistScreen } from "./screens/PlaylistScreen.js";
import { PlaylistsScreen } from "./screens/PlaylistsScreen.js";
import { SearchScreen } from "./screens/SearchScreen.js";
import { SettingsScreen } from "./screens/SettingsScreen.js";
import { StatisticsScreen } from "./screens/StatisticsScreen.js";
import { RankingsScreen } from "./screens/RankingsScreen.js";
import { Triage } from "./screens/Triage.js";
import { MobileWall } from "./screens/Wall.js";
import { useMobile } from "./useMobile.js";
import { endpoints } from "./api.js";
import { useResource } from "./useResource.js";
import shell from "./design/Shell.module.css";

/**
 * Real routes, replacing the `useState` view switcher.
 *
 * The reason this matters beyond tidiness: an album had no URL, so it could not be linked,
 * bookmarked, or reloaded — and `MOVE`, filters and rankings are all things the design expects to
 * be shareable as a link (PLAN.md §2, "search-param state so a ranking view is linkable").
 *
 * The booth is a route rather than a mode flag, so `R` is navigation and `Esc` is going back.
 */

function Shell() {
  const counts = useResource(() => endpoints.counts(), []);
  const navigate = useNavigate();

  // `R` from anywhere. The booth owns the keyboard once it is open, so this listens only outside.
  useLayoutEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      if (target && (target.tagName === "INPUT" || target.tagName === "TEXTAREA" || target.isContentEditable)) return;
      if (event.key === "r" || event.key === "R") {
        event.preventDefault();
        void navigate({ to: "/rate" });
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [navigate]);

  return (
    <div className={shell.shell}>
      <Rail counts={counts.data} note="PRESS R TO ENTER RATING MODE." />
      {/* A real landmark, not a div that looks like one: it is what "skip to content" and a
          screen reader's landmark list are for, and the app had none. */}
      <main className={shell.main}>
        {/*
          The bar carries the keyboard hint and nothing else.

          RANKINGS, SAME SONG? and IMPORT lived here as well, and all three are rows in the rail
          two inches to the left — a second, shorter copy of the map, disagreeing with the first
          about which four of fourteen destinations matter. The rail is the map; the bar is the
          search field and the one thing the rail cannot say, which is that R works from here.
        */}
        <TopBar context={<span>PRESS R TO RATE</span>} />
        <Outlet />
      </main>
    </div>
  );
}

const rootRoute = createRootRoute({ component: Shell });

/**
 * Four screens work on a phone (design/README.md §13). Everything else gets the wall.
 *
 * A gate at the ROUTE rather than a media query inside each screen, because "this is desktop work"
 * is a decision about the screen and not about its layout. A screen that merely reflowed badly
 * below 900px would still be reachable, which is exactly the outcome §13 rules out — *a wall, not
 * a bad small version*.
 *
 * The sentence is per screen, because "it does not fit" is not a reason anybody can act on and
 * "nested AND/OR groups and a live count need a keyboard" is.
 */
function desktopOnly(screen: string, because: string, Component: () => React.ReactElement | null) {
  return function Gated() {
    return useMobile() ? <MobileWall screen={screen} because={because} /> : <Component />;
  };
}

/**
 * `/` is the dashboard.
 *
 * It redirected to the rankings until Phase 6, because landing on an empty placeholder would have
 * been worse than landing on the thing the app is actually for. The reason for the redirect —
 * two routes sharing one component never remount, so the screen would not refetch between them —
 * no longer applies now that `/` renders something of its own.
 */
const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/",
  component: desktopOnly(
    "DASHBOARD",
    "The dashboard is four panels read at a glance, together. Stacked into one column it stops being a glance and becomes a list you scroll past.",
    DashboardScreen,
  ),
});

const rankingsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/rankings",
  component: RankingsScreen,
});

/**
 * `/album/7?song=91` opens the record with that track under the cursor.
 *
 * The library's song titles point here. There is no page for one song — a song lives on a record,
 * and the record is where you rate it — so the link says WHICH song rather than dropping you at
 * the top of a twenty-four track album to find it yourself.
 */
const albumRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/album/$albumId",
  validateSearch: (search: Record<string, unknown>): { song?: number } =>
    Number.isInteger(Number(search.song)) && Number(search.song) > 0 ? { song: Number(search.song) } : {},
  component: function AlbumRoute() {
    const { albumId } = useParams({ from: "/album/$albumId" });
    const { song } = useSearch({ from: "/album/$albumId" });
    return <AlbumScreen albumId={Number(albumId)} focusSongId={song} />;
  },
});

/**
 * The artist page. A URL of its own, because an artist is a thing you link somebody to — and
 * because the rankings table has been promising this link since Phase 4.
 */
const artistRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/artist/$artistId",
  component: desktopOnly(
    "ARTIST",
    "A hero, a row of covers and a table of every song they have — three readings of one artist, meant to be taken together.",
    function ArtistRoute() {
      const { artistId } = useParams({ from: "/artist/$artistId" });
      return <ArtistScreen artistId={Number(artistId)} />;
    },
  ),
});

/**
 * The library's tab is a URL segment, not component state, so `NEED TO RATE` is a link the rail
 * can point at and a view somebody can bookmark. An unknown tab redirects rather than rendering
 * an empty screen — a typed URL should land somewhere real.
 */
const libraryRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/library/$tab",
  /*
   * `?sort=artist&dir=desc`. In the URL for the same reason the tab is a path segment: an order
   * you chose is part of what you are looking at, so it survives a reload and it is a link.
   *
   * Neither is validated against the tab here — ALBUM is a column on Songs and not on Albums, and
   * the screen falls back to the tab's own sort rather than the route refusing to render. A
   * mistyped sort should land you on a library, not on an error.
   */
  validateSearch: (search: Record<string, unknown>): { sort?: string; dir?: SortDirection } => ({
    ...(typeof search.sort === "string" ? { sort: search.sort } : {}),
    ...(search.dir === "asc" || search.dir === "desc" ? { dir: search.dir } : {}),
  }),
  component: desktopOnly(
    "LIBRARY",
    "Eight columns over three and a half thousand rows, with a jump rail down the side. Browsing it needs the width — rating from it does not, and RATE works here.",
    function LibraryRoute() {
      const { tab } = useParams({ from: "/library/$tab" });
      const { sort, dir } = useSearch({ from: "/library/$tab" });
      const navigate = useNavigate();
      if (!LIBRARY_TABS.some((t) => t.tab === tab)) throw redirect({ to: "/library/$tab", params: { tab: "songs" } });
      return (
        <LibraryScreen
          tab={tab as LibraryTab}
          sort={sort}
          direction={dir}
          // A new tab drops the sort rather than carrying it: SCR on Songs and SCR on Artists are
          // different columns, and inheriting a column that does not exist is how you land on a
          // table sorted by nothing.
          onTab={(next) => void navigate({ to: "/library/$tab", params: { tab: next }, search: {} })}
          onSort={(next, direction) =>
            // `replace`, so turning a column around three times does not put three entries between
            // you and the screen you came from.
            void navigate({ to: "/library/$tab", params: { tab }, search: { sort: next, dir: direction }, replace: true })
          }
        />
      );
    },
  ),
});

/**
 * `/search?q=` — the query is in the URL, so a search is a link and reloading keeps it.
 */
const searchRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/search",
  validateSearch: (search: Record<string, unknown>) => ({ q: typeof search.q === "string" ? search.q : "" }),
  component: function SearchRoute() {
    const { q } = useSearch({ from: "/search" });
    const navigate = useNavigate();
    return (
      <SearchScreen
        q={q}
        // `replace`, so typing a query does not push twenty history entries between the screen
        // you came from and the one you are on.
        onQuery={(next) => void navigate({ to: "/search", search: { q: next }, replace: true })}
      />
    );
  },
});

/** `/lists?edit=<id>` opens a saved list in the builder — reached from the list's own screen. */
const listsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/lists",
  validateSearch: (search: Record<string, unknown>): { edit?: number } =>
    Number.isInteger(Number(search.edit)) && Number(search.edit) > 0 ? { edit: Number(search.edit) } : {},
  component: desktopOnly(
    "LISTS",
    "The rule builder is nested groups, a live count and a preview beside it. On a phone you lose the preview or you lose the tree, and the two together are the screen.",
    ListsScreen,
  ),
});

/**
 * A saved list, run now. The builder's preview shows twelve; this is what "412 songs match" was
 * promising — and the list had no way to be looked at at all before it.
 */
const listRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/list/$listId",
  component: desktopOnly(
    "LIST",
    "A table of everything the rules select, beside the rules themselves. It shares the wall with the builder it came from.",
    function ListRoute() {
      const { listId } = useParams({ from: "/list/$listId" });
      return <ListScreen listId={Number(listId)} />;
    },
  ),
});

/**
 * `/playlists?from=<id>` loads a saved playlist's recipe into the generator. A search param rather
 * than a route, because it is the same screen doing the same thing — and Keep then saves a NEW
 * playlist, which is the whole point: a snapshot stays a snapshot.
 */
const playlistsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/playlists",
  validateSearch: (search: Record<string, unknown>): { from?: number } =>
    Number.isInteger(Number(search.from)) && Number(search.from) > 0 ? { from: Number(search.from) } : {},
  component: desktopOnly(
    "PLAYLISTS",
    "A filter tree, a constraints block and the result side by side. The report only means anything next to the rules that produced it.",
    PlaylistsScreen,
  ),
});

/**
 * A kept playlist has a URL of its own. It used to have none: you could generate fifty tracks,
 * press Keep, and the only trace in the interface was a row count on the index.
 */
const playlistRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/playlist/$playlistId",
  component: desktopOnly(
    "PLAYLIST",
    "A track list, its recipe, and the run that produced them. It shares the wall with the generator it came from rather than being reachable from nowhere.",
    function PlaylistRoute() {
      const { playlistId } = useParams({ from: "/playlist/$playlistId" });
      return <PlaylistScreen playlistId={Number(playlistId)} />;
    },
  ),
});

const statisticsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/statistics",
  component: desktopOnly(
    "STATISTICS",
    "Four measures meant to be read against each other. One at a time down a phone is four screens, not a comparison.",
    StatisticsScreen,
  ),
});

const settingsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/settings",
  component: desktopOnly(
    "SETTINGS",
    "The consequence table beside the sliders is the whole point: moving M has to visibly move four real artists' scores while you watch.",
    SettingsScreen,
  ),
});

const triageRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/triage",
  component: desktopOnly(
    "SAME SONG?",
    "Two candidate recordings weighed against each other, with the releases each one appears on. It is a comparison, and a comparison needs both sides visible.",
    Triage,
  ),
});

const importRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/import",
  component: desktopOnly(
    "IMPORT",
    "A dry run you read before you commit — counts, skipped rows, duplicate groups and album promotion, all reviewable at once.",
    ImportScreen,
  ),
});

/**
 * The listening-history import, beside the library one rather than inside it. They read different
 * files and answer different questions — what you own, and what you actually play.
 */
const listeningRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/import/history",
  component: desktopOnly(
    "IMPORT",
    "Choosing a folder of files and reading a match rate before committing. Desktop work, and so is the download it starts from.",
    ListeningScreen,
  ),
});

const specimenRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/specimen",
  component: function SpecimenRoute() {
    return (
      <div className={shell.content}>
        <Specimen />
      </div>
    );
  },
});

/**
 * The booth is deliberately OUTSIDE the shell layout — direction 7A: rating mode keeps the rail
 * but loses the page chrome, and the booth renders its own.
 */
const QUEUES = ["oldest", "most-played"] as const;

const rateRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/rate",
  /*
   * `?queue=most-played` orders the same queue by what the listening history says you keep going
   * back to. A search param rather than a mode, so it is a link the import receipt can point at
   * and a sitting somebody can bookmark.
   */
  // Optional on purpose: `/rate` is linked from four places and the ordinary sitting has no
  // param at all, so an absent one must not become a thing every caller has to write.
  validateSearch: (search: Record<string, unknown>): { queue?: (typeof QUEUES)[number] } =>
    QUEUES.includes(search.queue as (typeof QUEUES)[number]) ? { queue: search.queue as (typeof QUEUES)[number] } : {},
  component: function RateRoute() {
    const { queue } = useSearch({ from: "/rate" });
    return <BoothScreen strategy={queue} />;
  },
});

const tree = rootRoute.addChildren([
  indexRoute,
  rankingsRoute,
  albumRoute,
  artistRoute,
  libraryRoute,
  searchRoute,
  listsRoute,
  listRoute,
  playlistsRoute,
  playlistRoute,
  statisticsRoute,
  settingsRoute,
  importRoute,
  listeningRoute,
  triageRoute,
  specimenRoute,
  rateRoute,
]);

export const router = createRouter({ routeTree: tree });

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

export function Routes() {
  return <RouterProvider router={router} />;
}
